import { Button, Divider, Popconfirm, Space, Spin, Tag, Typography, theme } from 'antd';
import { FC } from 'react';
import { BoxArrowUpRight, ExclamationCircle, PencilSquare, Trash } from 'react-bootstrap-icons';

import { HostingsAccountHiddenPassword } from '@/modules/Hostings/components/HostingsAccountHiddenPassword';
import { ProxyServerHiddenPassword } from '@/modules/ProxyServers/components/ProxyServerHiddenPassword/ProxyServerHiddenPassword';
import { CopyableText, DescriptionBlock, HiddenPassword } from '../../../../../common/components';
import { AppRoutes } from '../../../../../common/interfaces';
import { CommonDrawerTitle } from '../../../../../common/layouts';
import { ProxyServer } from '../../../entities';
import { ProxyServerConfigurationStatuses, ProxyServerHealthStatuses } from '../../../proxyServerAddresses.interfaces';
import { ProxyServerAddressesList } from '../../ProxyServerAddresses';

interface Props {
  proxyServer: ProxyServer;
  deleteButtonLoading: boolean;
  checkAddressesButtonLoading: boolean;
  disableButtons: boolean;
  handleDeleteProxyServer: () => void;
  handleCheckAddressesProxyServer: () => void;
  handleEditProxyServer: () => void;
}

const { Text } = Typography;

export const ProxyServersOverviewView: FC<Props> = ({
  proxyServer,
  deleteButtonLoading,
  checkAddressesButtonLoading,
  disableButtons,
  handleDeleteProxyServer,
  handleCheckAddressesProxyServer,
  handleEditProxyServer,
}) => {
  const { token } = theme.useToken();

  return (
    <>
      <CommonDrawerTitle
        onlyExtra
        extra={
          <Space>
            <Button disabled={disableButtons} icon={<PencilSquare />} onClick={handleEditProxyServer}>
              Редагувати
            </Button>
            <Popconfirm
              disabled={disableButtons}
              title="Видалити цей проксі-сервер?"
              onConfirm={handleDeleteProxyServer}
              okType="danger"
              okText="Так"
              icon={<ExclamationCircle color={token.colorError} />}
              placement="bottomRight"
            >
              <Button loading={deleteButtonLoading} disabled={disableButtons} danger icon={<Trash />}/>
            </Popconfirm>
          </Space>
        }
      />

      <DescriptionBlock items={[['Назва', proxyServer.name]]} />
      <DescriptionBlock items={[['Пароль', <ProxyServerHiddenPassword proxyServerId={proxyServer.id} />]]} />
      <DescriptionBlock
        items={[
          [
            'Хостинг',
            <a
              key="proxyServer"
              target="_blank"
              href={AppRoutes.HostingsById.replace(':hostingId', proxyServer.hosting.id)}
              rel="noreferrer"
            >
              {proxyServer.hosting.name}&nbsp;
              <BoxArrowUpRight />
            </a>,
          ],
        ]}
      />
      <DescriptionBlock
        items={[
          [
            'Акаунт хостингу',
            <div key="hostingAccount" style={{ width: '100%' }}>
              <div style={{ display: 'flex', width: '100%' }}>
                <Text type="secondary" style={{ fontWeight: 400, wordBreak: 'keep-all' }}>
                  Логін:&nbsp;
                </Text>
                <CopyableText textToCopy={proxyServer.hostingAccount.login} style={{ maxWidth: 265, width: '100%', margin: 0 }}>
                  {proxyServer.hostingAccount.login}
                </CopyableText>
              </div>
              <div style={{ display: 'flex', width: '100%' }}>
                <Text type="secondary" style={{ fontWeight: 400, wordBreak: 'keep-all' }}>
                  Пароль:&nbsp;
                </Text>
                <HostingsAccountHiddenPassword
                  hostingId={proxyServer.hostingAccount.hostingId}
                  hostingAccountId={proxyServer.hostingAccount.id}
                />
              </div>
            </div>,
          ],
        ]}
      />
      <DescriptionBlock items={[['IP адреси', <ProxyServerAddressesList key="addresses" addresses={proxyServer.addresses} />]]} />

      <Divider />

      <DescriptionBlock
        layout="horizontal"
        items={[
          [
            'Перевірка доступності',
            <div key="status" style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'flex-end' }}>
              <Button
                type="dashed"
                shape="round"
                size="small"
                loading={checkAddressesButtonLoading}
                disabled={disableButtons}
                onClick={handleCheckAddressesProxyServer}
              >
                оновити всі
              </Button>
              &emsp;
              {!!proxyServer.addresses.filter(
                (el) =>
                  el.healthStatus === ProxyServerHealthStatuses.Checking ||
                  el.configurationStatus === ProxyServerConfigurationStatuses.Checking,
              ).length && (
                <Tag>
                  <Spin size="small" spinning style={{ margin: '2px 4px' }} />
                </Tag>
              )}
              {!proxyServer.addresses.filter(
                (el) =>
                  el.healthStatus === ProxyServerHealthStatuses.Checking ||
                  el.configurationStatus === ProxyServerConfigurationStatuses.Checking,
              ).length && (
                <Tag
                  color={
                    proxyServer.addresses.filter(
                      (el) =>
                        el.healthStatus === ProxyServerHealthStatuses.Working &&
                        el.configurationStatus === ProxyServerConfigurationStatuses.Configured,
                    ).length
                      ? 'success'
                      : 'error'
                  }
                >
                  {
                    proxyServer.addresses.filter(
                      (el) =>
                        el.healthStatus === ProxyServerHealthStatuses.Working &&
                        el.configurationStatus === ProxyServerConfigurationStatuses.Configured,
                    ).length
                  }
                  &nbsp;/&nbsp;
                  {proxyServer.addresses.length}
                </Tag>
              )}
            </div>,
          ],
        ]}
      />
    </>
  );
};
